/**
 * The two things a surface does to change which conversation is on screen: open a stored one, or
 * start a fresh one.
 *
 * Both used to live inside the `/chat` history sidebar's own components — the row-click
 * rehydration in `chat-history-list.tsx`, the reset in `chat-history-sidebar.tsx`. PLT-6529 adds
 * a second surface offering the same two actions: the unified investor rail's Chats group
 * (`views/unified-shell/unified-investor-navigation.tsx`), which is outside this directory and
 * can't reach into either component.
 *
 * Moving them here is what keeps the two surfaces doing the *same* thing rather than two things
 * that agree today. `rehydrateConversation` in particular carries the fixes of PLT-5019 (build
 * the whole repository up front, don't append message by message), PLT-5137 (retry, then toast
 * instead of failing silently), PLT-5232 (build before you blank the surface) and PLT-5240 (await
 * the thread switch). A second copy in the rail would lose those one at a time.
 *
 * Both take the runtime as an argument rather than calling `useAssistantRuntime()` themselves, so
 * this file stays React-free and callable straight from an event handler.
 */
import { ExportedMessageRepository, type AssistantRuntime } from '@assistant-ui/react';
import { type QueryClient } from '@tanstack/react-query';
import { errorToast } from '@upright/ui/common/generic/components/toast';
import { type ChatConversationDetail, fetchChatConversationDetail } from './chat-history-data';
import { setActiveConversationId } from './chat-history-state';
import {
  composerState,
  requestComposerFocus,
  resetChatLoggingState,
  resetChatLoggingStateKeepingCompany,
} from '../upright-chat-shared';
// PLT-5514: the seed builders moved to a shared module so the live-injection
// path (`use-conversation-events.ts`) reuses the exact same reconstruction.
import { buildSeededRepository } from './seed-message';

/**
 * Start a fresh conversation on the shared runtime.
 *
 * `isConsultant` decides whether the client-company scoping survives the reset — see
 * `resetChatLoggingStateKeepingCompany`. Callers get it from
 * `isDmaConsultantUser(useGetLoggedInUser())`.
 *
 * Does not navigate. A caller already on `/chat` wants only the reset; a caller elsewhere (the
 * investor rail) pairs this with its own link to `/chat`, so the reset happens in the click and
 * the navigation happens through the router.
 */
export function startNewChat({ runtime, isConsultant }: { runtime: AssistantRuntime; isConsultant: boolean }): void {
  // Keep the client-company scoping across `New chat` (PLT-5256): a fresh
  // conversation stays attributed to the same company the consultant is
  // working inside, rather than dropping it and tripping the backend
  // off-company guard on the next turn.
  resetChatLoggingStateKeepingCompany(isConsultant);
  runtime.switchToNewThread();
  // resetChatLoggingState already rotates `composerState.threadId` to a
  // fresh uuid; that fresh id won't have a row yet so nothing in the
  // sidebar should be highlighted.
  setActiveConversationId(null);
  // A new chat ends with the cursor in the composer. The composer's own
  // `autoFocus` covers a caller arriving from another page; this covers the
  // ones where nothing remounts, which is every caller already looking at a
  // chat (PLT-6766).
  requestComposerFocus();
}

/**
 * PLT-4883: seed the assistant-ui runtime with a past conversation.
 *
 * Why `runtime.thread.import(...)` rather than the
 * `useLocalRuntime({ initialMessages })` option: the runtime is mounted
 * once at the app root (`ChatRuntimeProvider`) and shared between the
 * side panel and the `/chat` route, so we can't remount it on every row
 * click without tearing down the whole chat subtree and any in-flight
 * panel session below it. `runtime.thread.import` is the in-place
 * equivalent — it replaces the current (freshly switched-to) thread's
 * message tree wholesale, without a remount and without triggering a
 * model run, which is exactly the seeding behaviour we want.
 *
 * PLT-5019: seed the whole conversation *atomically* via `import` rather
 * than appending messages one at a time. The previous implementation
 * looped `runtime.thread.append(..., { startRun: false })` and yielded a
 * single microtask (`await Promise.resolve()`) between appends.
 * assistant-ui resolves each appended message's parentId from
 * `messages.at(-1)?.id` at call time, but a single microtask is not
 * enough for the LocalRuntime to commit the prior append and advance its
 * head pointer, so the second message resolved its parent to the *root*
 * instead of the first message. The initial user prompt (`user0`) and the
 * first assistant reply (`asst0`) therefore landed as *sibling root
 * branches* rather than a linear chain — the thread rendered the latest
 * branch (the assistant reply) and parked the initial prompt on a hidden
 * alternate branch (the "‹ 2 / 2 ›" branch pager), so it looked like the
 * prompt was never there. `ExportedMessageRepository.fromArray` wires
 * each message's parentId to the previous one and `import` sets the head
 * to the resulting leaf, producing a guaranteed linear chain and removing
 * the parent-resolution race entirely.
 *
 * PLT-4947: assistant messages now replay their MCP App cards. When the
 * persisted row carries `contentEvents` (the lean rehydration projection
 * the backend derives in `persistChatTurn` — assistant prose + card
 * tool-calls with their structured result), `buildSeededMessage`
 * reconstructs assistant-ui `tool-call` content parts with
 * `result: { data, _ui }`, mirroring the live adapter
 * (`upright-chat-adapter.ts`), so `McpToolUI` remounts the card iframe.
 * Messages without `contentEvents` (older rows, user messages, or turns
 * with no cards) fall back to the single plain-text part — the `content`
 * column is the rendered assistant text + the user's typed prompt, which
 * is enough to read the past conversation. Other rich per-event UI
 * (citations, generated reports, attachments) is still not replayed —
 * rebuilding those from stored events stays a separate project. For
 * follow-up turns the model still has access to its own past responses
 * because each turn is sent back through the chat adapter, which
 * re-serialises the runtime's full message list to `/api/chat`.
 *
 * The `rehydrationTokenRef` guard makes this safe against double-clicks:
 * the moment the user clicks another row, the caller rewrites the
 * ref to the new id, and the checkpoints after the (async) fetch and after
 * the awaited thread switch see the mismatch and exit early — without that,
 * two rapid clicks would race and the loser's messages could land on top of
 * the winner's seeded thread. A caller with only one conversation to open (the
 * `?conversationId=` deep link, PLT-6529) passes a ref holding that id: nothing
 * ever moves it, so every checkpoint passes.
 *
 * PLT-7427: when the shared runtime already holds `id` — the user left `/chat/<id>` for a profile
 * and came back with Back or the rail — nothing is imported, because the live thread is richer
 * than the stored copy. The stored copy leaves out the chat-only cards that are not replayed
 * (`REPLAYABLE_UI_ONLY_TOOLS` in `chat-transcript/parser.ts`: the tailoring cards, follow-up
 * suggestions), and the thread switch would end a turn that is still streaming. A card's own state
 * is not kept either way: the cards unmount with the chat view, so edits made in a card and not yet
 * saved are gone after the visit. Messages a background job posted while the user was away still
 * arrive: `useLiveConversationInjection` reconciles when the chat remounts. The chat-side state is
 * reset all the same, so the visit to a profile, which republishes the page binding and lens, does
 * not carry into the next turn of this conversation.
 */
export async function rehydrateConversation({
  id,
  runtime,
  queryClient,
  rehydrationTokenRef,
}: {
  id: string;
  runtime: AssistantRuntime;
  queryClient: QueryClient;
  rehydrationTokenRef: { current: string | null };
}): Promise<void> {
  if (composerState.threadId === id && runtime.thread.getState().messages.length > 0) {
    const { threadId, messageIndex } = composerState;
    resetChatLoggingState();
    composerState.threadId = threadId;
    composerState.messageIndex = messageIndex;
    setActiveConversationId(id);
    return;
  }

  let detail: ChatConversationDetail;
  try {
    detail = await fetchChatConversationDetail(queryClient, id);
  } catch (err) {
    // PLT-5137: the fetch (which now retries transient failures and the
    // optimistic-insert 410 internally — see
    // `fetchConversationDetailWithRetry`) has exhausted its attempts. Leave
    // the runtime untouched so the user keeps whatever thread they had open,
    // but no longer fail *silently*: previously this swallowed the error and
    // left the user on the empty landing page with the row still highlighted
    // and no feedback, which is exactly the "clicking history sometimes does
    // nothing" symptom. Surface a toast so the click has a visible outcome
    // and the user knows to try again.
    //
    // Only toast if this is still the active selection: if the user has
    // since clicked another row, `rehydrationTokenRef` has moved on and a
    // toast for the abandoned click would be noise.
    if (rehydrationTokenRef.current === id) {
      errorToast('Couldn’t open that conversation. Please try again.');
    }
    if (import.meta.env.DEV) {
      console.warn('[chat-history] failed to load conversation', id, err);
    }
    return;
  }

  // Bail if the user has clicked elsewhere while we were fetching.
  if (rehydrationTokenRef.current !== id) return;

  // PLT-5232: build the assistant-ui repository *before* touching the runtime,
  // and seed inside a try/catch.
  //
  // `ExportedMessageRepository.fromArray` runs assistant-ui's
  // `fromThreadMessageLike` over every message and throws on shapes it rejects
  // — e.g. an assistant turn whose only text part is empty/non-string, or a
  // `contentEvents` projection that reconstructs a part type it doesn't accept.
  // Whether a given stored conversation hits one of those shapes is
  // content-dependent, which is why it presents as "some open, some don't".
  //
  // PLT-5137 wrapped only the *fetch* above. The seeding ran unguarded, and —
  // critically — `switchToNewThread()` blanked the surface to the welcome page
  // *first*, so a throw here left the user stranded on the empty landing page
  // with the row still highlighted and *no* toast (the exact symptom Milja
  // reported on persisted conversations after PLT-5137 shipped). Constructing
  // the repository first means the throw happens before we blank anything: the
  // user keeps the thread they had, and we surface the same error toast plus a
  // logged error. The log carries the real thrown error + conversation id so the
  // exact per-conversation trigger is identifiable — we deliberately do *not*
  // sanitise the message shape here, since that would hide the trigger.
  let seededRepository: ReturnType<typeof ExportedMessageRepository.fromArray>;
  try {
    // Building the full repository up front also guarantees a linear parent
    // chain (`user0 -> asst0 -> user1 -> ...`) and avoids the microtask race
    // the old incremental-append loop hit (PLT-5019). See the rationale block
    // above `rehydrateConversation`.
    //
    // `buildSeededRepository` (shared with the live-injection path, PLT-5514)
    // validates each turn against assistant-ui in isolation, so one
    // unconvertible message degrades to a placeholder rather than aborting the
    // whole import (PLT-5268). The outer try/catch stays as a final backstop
    // for chain-level guards that only fire on the full array.
    seededRepository = buildSeededRepository(detail.messages, id);
  } catch (err) {
    // Only toast if this is still the active selection (see the fetch-failure
    // branch above for the rationale).
    if (rehydrationTokenRef.current === id) {
      errorToast('Couldn’t open that conversation. Please try again.');
    }
    console.error('[chat-history] failed to seed conversation', { conversationId: id, error: err });
    return;
  }

  // Reset the chat-side state for a brand-new view of an old thread.
  // `resetChatLoggingState` rotates `threadId` to a fresh uuid, clears
  // the page-context chip, the debug store, and the hidden entity
  // context — all of which would otherwise leak from whatever the
  // user was doing before they clicked the row. We then overwrite
  // `threadId` with the actual conversation id and set
  // `messageIndex` to the count of seeded messages, so the next user
  // turn is logged as the next index of the *server-stored*
  // conversation (not a fresh thread keyed on a brand-new uuid).
  //
  // Safe to blank the surface now: the repository above was built without
  // throwing, so the `import` below won't strand the user on the welcome page.
  resetChatLoggingState();
  // PLT-5240: the thread switch is deferred behind a microtask, so seeding
  // synchronously after it raced — `import` landed on the thread we were
  // leaving while the switch repointed `runtime.thread` to a fresh empty one,
  // producing the saved-chat ⇄ empty-composer toggle on repeated clicks. Await
  // the switch so `import` always seeds the displayed thread. Mirrors the
  // already-merged `InitialPromptAutoSender` fix; uses the non-deprecated
  // `runtime.threads.switchToNewThread()` (the top-level alias is typed `void`).
  try {
    await runtime.threads.switchToNewThread();
  } catch (err) {
    // Fall through and seed the current thread rather than leaving it blank.
    if (import.meta.env.DEV) {
      console.warn('[chat-history] switchToNewThread failed; seeding current thread', id, err);
    }
  }
  // Re-check the double-click guard: the user may have clicked another row
  // during the awaited switch (see the fetch/seed bail-outs above).
  if (rehydrationTokenRef.current !== id) return;
  runtime.thread.import(seededRepository);

  composerState.threadId = id as `${string}-${string}-${string}-${string}-${string}`;
  composerState.messageIndex = detail.messages.length;
  composerState.pendingSource = 'user_input';
  // Say again which conversation is open, now that the seeding has settled. The caller already
  // said so before the fetch, for responsiveness, but the switch above ends whatever turn was
  // streaming, and the turn-finished handler writes the running thread's id — which is the fresh
  // uuid `resetChatLoggingState` has just rotated to, belonging to no conversation. Without this
  // line, opening a conversation while an answer is still streaming leaves every row in the list
  // unmarked (PLT-6610).
  setActiveConversationId(id);
}
